import type { ReactNode } from 'react';
import { Fragment } from 'react';
import { useTranslation } from 'react-i18next';

import { MenuItem, MenuList, SectionDivider } from '@adflow/frontend/ui-common';
import { typography } from '@adflow/frontend/ui-theme';
import type { ViewColumnTemplateDto } from '@adflow/shared/util-column-templates';

import { ColumnTemplateOption } from './ColumnTemplateOption';
import type { ColumnTemplateId, SelectColumnTemplate } from './types';

interface ColumnTemplateMenuListProps {
	createNewColumnTemplate: () => void;
	setOpen: (value: boolean) => void;
	activeColumnTemplateId: ColumnTemplateId | undefined;
	selectColumnTemplate: SelectColumnTemplate;
	openColumnTemplateEditor: (templateId: ColumnTemplateId) => void;
	templates: ViewColumnTemplateDto[][];
}

export function ColumnTemplateMenuList({
	createNewColumnTemplate,
	setOpen,
	selectColumnTemplate,
	openColumnTemplateEditor: propsOpenColumnTemplateEditor,
	activeColumnTemplateId,
	templates,
}: ColumnTemplateMenuListProps) {
	const [
		savedAllColumnTemplates,
		savedColumnTemplates,
		defaultColumnTemplates,
	] = templates;

	const { t } = useTranslation('common');

	const openColumnTemplateEditor = (templateId: ColumnTemplateId) => {
		propsOpenColumnTemplateEditor(templateId);
		setOpen(false);
	};

	const sections: ReactNode[] = [];

	sections.push(
		<MenuItem
			onSelect={() => selectColumnTemplate(undefined)}
			css={typography.clickableLink}
			data-testid="default-column-template"
			key="default-column-template"
		>
			{t('columnTemplate.menu.reset')}
		</MenuItem>,
		<MenuItem
			onSelect={() => {
				createNewColumnTemplate();
			}}
			css={typography.clickableLink}
			data-testid="new-column-template"
			key="new-column-template"
		>
			{t('columnTemplate.menu.new')}
		</MenuItem>
	);
	if (defaultColumnTemplates!.length > 0) {
		sections.push(
			<Fragment key="default-column-templates">
				<SectionDivider text={t('columnTemplate.menu.group.default')} />

				{[...defaultColumnTemplates!]
					.sort((a, b) => a.name.localeCompare(b.name))
					.map((template) => (
						<ColumnTemplateOption
							key={template.id}
							template={template}
							selectColumnTemplate={selectColumnTemplate}
							openColumnTemplateEditor={openColumnTemplateEditor}
							selected={template.id === activeColumnTemplateId}
						/>
					))}
			</Fragment>
		);
	}
	if (savedAllColumnTemplates!.length > 0) {
		sections.push(
			<Fragment key="saved-all-templates">
				<SectionDivider text={t('columnTemplate.menu.group.global')} />

				{savedAllColumnTemplates!
					.sort((a, b) => a.name.localeCompare(b.name))
					.map((template) => (
						<ColumnTemplateOption
							key={template.id}
							template={template}
							selectColumnTemplate={selectColumnTemplate}
							openColumnTemplateEditor={openColumnTemplateEditor}
							selected={template.id === activeColumnTemplateId}
						/>
					))}
			</Fragment>
		);
	}

	if (savedColumnTemplates!.length > 0) {
		sections.push(
			<Fragment key="saved-templates">
				<SectionDivider text={t('columnTemplate.menu.group.saved')} />

				{savedColumnTemplates!.map((template) => (
					<ColumnTemplateOption
						key={template.id}
						template={template}
						selectColumnTemplate={selectColumnTemplate}
						openColumnTemplateEditor={openColumnTemplateEditor}
						selected={template.id === activeColumnTemplateId}
					/>
				))}
			</Fragment>
		);
	}

	return (
		<MenuList
			css={{
				maxWidth: 600,
			}}
		>
			{sections.length > 0 ? (
				sections
			) : (
				<MenuItem css={typography.emptyLarge}>
					{t('columnTemplate.menu.noOptions')}
				</MenuItem>
			)}
		</MenuList>
	);
}
